/*
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import {
	FloatingPanelModel,
	IFloatingPanelRect,
	TGetFloatingPanelOpenParams
} from "../components/FloatingPanel";
import { offEvent, onEvent } from "@adapptio/lib-shared";

export interface IUseFloatingPanelModel<Params> {
	params: Params;
	close: () => void;
	rect: IFloatingPanelRect;
	setRect: (rect: IFloatingPanelRect) => void;
}

export function useFloatingPanelModel<
	FPModel extends FloatingPanelModel,
	Params = TGetFloatingPanelOpenParams<FPModel>
>(floatingPanelModel: FPModel): IUseFloatingPanelModel<Params> {
	const params = useMemo<Params>(() => floatingPanelModel.getParams() as Params, [ floatingPanelModel ]);
	const [ rectState, setRectState ] = useState(() => floatingPanelModel.getRect());

	const close = useCallback<IUseFloatingPanelModel<Params>["close"]>(() => {
		floatingPanelModel.close();
	}, [ floatingPanelModel ]);

	const setRect = useCallback<(rect: IFloatingPanelRect) => void>(
		(rect) => {
			floatingPanelModel.updateRect(rect);
		},
		[ floatingPanelModel ]
	);

	useEffect(() => {
		const updateRectState = () => {
			setRectState(floatingPanelModel.getRect());
		};

		onEvent(floatingPanelModel.onRectChange, updateRectState);

		return () => {
			offEvent(floatingPanelModel.onRectChange, updateRectState);
		};
	}, [ floatingPanelModel ]);

	return { params, close, rect: rectState, setRect };
}
